import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  CheckCircle2,
  ChevronDown,
  ArrowUpRight,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import WhyHire from "@/components/WhyHire";
import ConsultationForm from "@/components/ConsultationForm";
import Seo from "@/components/Seo";
import { PRACTICE_AREAS, FIRM } from "@/lib/site";

export default function PracticeAreaDetail() {
  const { slug } = useParams();
  const area = PRACTICE_AREAS.find((a) => a.slug === slug);
  const [openFaq, setOpenFaq] = useState(0);

  if (!area) return <Navigate to="/practice-areas" replace />;

  const Icon = area.icon;
  const related = PRACTICE_AREAS.filter((a) => a.slug !== slug).slice(0, 3);

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: area.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div data-testid={`practice-area-page-${area.slug}`} className="bg-white">
      <Seo
        title={area.metaTitle}
        description={area.metaDescription}
        path={`/practice-areas/${area.slug}`}
        jsonLd={faqJsonLd}
      />

      {/* HERO */}
      <section className="relative bg-navy-50 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <nav className="flex items-center gap-2 text-xs text-navy-900/55 mb-8">
              <Link to="/" className="hover:text-navy-900">
                Home
              </Link>
              <span>/</span>
              <Link to="/practice-areas" className="hover:text-navy-900">
                Practice Areas
              </Link>
              <span>/</span>
              <span className="text-gold">{area.title}</span>
            </nav>
            <div className="h-14 w-14 rounded-sm bg-gold/10 grid place-items-center border border-gold/20">
              <Icon className="h-6 w-6 text-gold" />
            </div>
            <h1 className="font-display text-4xl md:text-6xl lg:text-[4.5rem] leading-[1.02] tracking-tight text-navy-900 mt-6 max-w-4xl text-balance">
              {area.title}
            </h1>
            <p className="mt-6 max-w-2xl text-lg md:text-[1.15rem] text-navy-900/65 leading-relaxed font-light">
              {area.short}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="practice-hero-consult"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-sm bg-gold text-navy-950 text-sm font-semibold hover:bg-gold-dark transition-all"
              >
                Free Consultation
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={`tel:${FIRM.phoneHref}`}
                data-testid="practice-hero-call"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-sm border-2 border-navy-900 text-navy-900 text-sm font-semibold hover:bg-navy-900 hover:text-white transition-all"
              >
                <Phone className="h-4 w-4 text-gold" />
                Call Now
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OVERVIEW + CASE TYPES */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-7">
              <SectionHeading
                eyebrow="Overview"
                title={`${area.title} representation in Massachusetts`}
                testId="practice-overview"
              />
              <Reveal delay={0.1}>
                <p className="mt-8 text-lg md:text-xl text-navy-900/70 leading-relaxed font-light">
                  {area.description}
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="rounded-sm border border-navy-900/10 bg-navy-50 p-8">
                  <div className="eyebrow text-gold">Common Case Types</div>
                  <ul className="mt-6 space-y-4">
                    {area.caseTypes.map((c, i) => (
                      <li
                        key={i}
                        data-testid={`case-type-${i}`}
                        className="flex items-start gap-3 text-navy-900/85"
                      >
                        <CheckCircle2 className="h-5 w-5 text-gold mt-0.5 flex-shrink-0" />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-20 md:py-28 bg-navy-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <WhyHire
            eyebrow="Why Choose Attorney Mahaney"
            title={`Why hire Joseph M. Mahaney for your ${area.title.toLowerCase()} case?`}
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="FAQ"
            title="Frequently asked questions"
            testId="practice-faq"
          />
          <div className="mt-12 space-y-4">
            {area.faqs.map((f, i) => {
              const open = openFaq === i;
              return (
                <Reveal key={i} delay={i * 0.05}>
                  <div
                    data-testid={`faq-item-${i}`}
                    className="rounded-sm border border-navy-900/10 bg-white overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(open ? -1 : i)}
                      data-testid={`faq-toggle-${i}`}
                      aria-expanded={open}
                      className="w-full flex items-center justify-between gap-4 text-left px-6 py-5"
                    >
                      <span className="font-display text-lg md:text-xl text-navy-900 leading-snug">
                        {f.q}
                      </span>
                      <ChevronDown
                        className={
                          "h-5 w-5 text-gold flex-shrink-0 transition-transform duration-300 " +
                          (open ? "rotate-180" : "")
                        }
                      />
                    </button>
                    <div
                      className={
                        "grid transition-all duration-400 " +
                        (open
                          ? "grid-rows-[1fr] opacity-100"
                          : "grid-rows-[0fr] opacity-0")
                      }
                    >
                      <div className="overflow-hidden">
                        <p className="px-6 pb-6 text-navy-900/65 leading-relaxed">
                          {f.a}
                        </p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONTACT CTA + FORM */}
      <section className="py-20 md:py-28 bg-navy-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="Free Consultation"
                title={`Discuss your ${area.title.toLowerCase()} matter.`}
                body="Tell Attorney Mahaney about your situation. Every inquiry is reviewed personally — and your consultation is free."
                testId="practice-cta"
              />
              <Reveal delay={0.1}>
                <a
                  href={`tel:${FIRM.phoneHref}`}
                  data-testid="practice-cta-call"
                  className="inline-flex items-center gap-2 mt-8 px-7 py-4 rounded-sm bg-gold text-navy-950 text-sm font-semibold hover:bg-gold-dark transition-all"
                >
                  <Phone className="h-4 w-4" />
                  Call {FIRM.phone}
                </a>
              </Reveal>

              {/* Related areas */}
              <Reveal delay={0.15}>
                <div className="mt-12">
                  <div className="eyebrow text-gold">Related Practice Areas</div>
                  <ul className="mt-5 space-y-3">
                    {related.map((r) => (
                      <li key={r.slug}>
                        <Link
                          to={`/practice-areas/${r.slug}`}
                          data-testid={`related-area-${r.slug}`}
                          className="group flex items-center justify-between gap-3 rounded-sm border border-navy-900/10 bg-white px-5 py-4 hover:border-gold/50 transition-colors"
                        >
                          <span className="text-navy-900 font-medium">
                            {r.title}
                          </span>
                          <ArrowUpRight className="h-4 w-4 text-gold" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <ConsultationForm
                  defaultPracticeArea={area.title}
                  testId="practice-consultation-form"
                />
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
